import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"
import { ApiError } from "../../features/shared/ApiError"
import { ErrorState, ReferralPacket, ReferralSummary } from "./schema"
import { apiService } from "../../features/shared/apiService"
import { RootState } from "../../reduxStore/store"
import { CACHE_KEY_REFERRAL_PACKET } from "../Account/userSliceCacheKeys"

const defaultErrorState: ErrorState = {
  message: "Failed to fetch data",
  statusCode: 500,
  errorDetails: {},
}

interface ReferralPacketState {
  referralPacket: ReferralPacket | null
  fetchedReferral: ReferralSummary | null
  loading: boolean
  error: ErrorState | null
}

const initialState: ReferralPacketState = {
  referralPacket: null,
  fetchedReferral: null,
  loading: false,
  error: null,
}

const canUseCachedReferralPacket = (
  packet: ReferralPacket,
  includeMemberIntake: boolean,
) => !includeMemberIntake || packet.memberIntake?.hasM3Data === true

export const fetchReferralByIdThunk = createAsyncThunk(
  "referral/fetchReferralById",
  async ({ referralId }: { referralId: string }, { rejectWithValue }) => {
    try {
      const referralSummary =
        await apiService.referral.get.REFERRAL_SUMMARY_BY_ID(referralId)
      return referralSummary
    } catch (error) {
      if (error instanceof ApiError) {
        return rejectWithValue({
          message: error.message,
          statusCode: error.statusCode,
          errorDetails: error.errorDetails,
        })
      } else if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue({
          message: error.response.data?.message || error.message,
          statusCode: error.response.status,
          errorDetails: error.response.data,
        })
      } else if (error instanceof Error) {
        return rejectWithValue({
          message: error.message,
          statusCode: 500,
          errorDetails: {},
        })
      } else {
        return rejectWithValue({
          message: "An unknown error occurred",
          statusCode: 500,
          errorDetails: {},
        })
      }
    }
  },
)

export const fetchReferralPacket = createAsyncThunk(
  "referralPacket/fetchReferralPacket",
  async (
    {
      caseId,
      useCache,
      includeMemberIntake = false,
    }: { caseId: string; useCache: boolean; includeMemberIntake?: boolean },
    { rejectWithValue },
  ) => {
    let completedSessionCount: number = 0

    completedSessionCount =
      await apiService.referral.get.COMPLETED_SESSION_COUNT_BY_CASEID(caseId)

    if (useCache) {
      const cache = localStorage.getItem(CACHE_KEY_REFERRAL_PACKET)
      if (cache) {
        try {
          const packet = JSON.parse(cache) as ReferralPacket
          if (canUseCachedReferralPacket(packet, includeMemberIntake)) {
            packet.completedSessionCount = completedSessionCount
            return packet as any
          }
        } catch {
          // If cache is corrupted, ignore and fetch from API
        }
      }
    }
    try {
      const packet = (await apiService.referral.get.REFERRAL_PACKET_BY_CASEID(
        caseId,
        includeMemberIntake,
      )) as ReferralPacket
      packet.completedSessionCount = completedSessionCount
      return packet as any
    } catch (error) {
      if (error instanceof ApiError) {
        return rejectWithValue({
          message: error.message,
          statusCode: error.statusCode,
          errorDetails: error.errorDetails,
        })
      } else if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue({
          message: error.response.data?.message || error.message,
          statusCode: error.response.status,
          errorDetails: error.response.data,
        })
      } else if (error instanceof Error) {
        return rejectWithValue({
          message: error.message,
          statusCode: 500,
          errorDetails: {},
        })
      } else {
        return rejectWithValue({
          message: "An unknown error occurred",
          statusCode: 500,
          errorDetails: {},
        })
      }
    }
  },
)

const referralPacketSlice = createSlice({
  name: "referralPacket",
  initialState,
  reducers: {
    resetState(state) {
      state.referralPacket = null
      state.fetchedReferral = null
      state.loading = false
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReferralPacket.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchReferralPacket.fulfilled, (state, action) => {
        state.referralPacket = action.payload
        state.loading = false
        state.error = null
      })
      .addCase(fetchReferralPacket.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
          ? { ...defaultErrorState, ...action.payload }
          : defaultErrorState
      })
      .addCase(fetchReferralByIdThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchReferralByIdThunk.fulfilled, (state, action) => {
        state.fetchedReferral = action.payload
        state.loading = false
        state.error = null
      })
      .addCase(fetchReferralByIdThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
          ? { ...defaultErrorState, ...action.payload }
          : defaultErrorState
      })
  },
})

export const selectReferralPacket = (state: RootState) => {
  return state.referralPacket
}

export const selectFetchedReferral = (state: RootState) => {
  return state.referralPacket.fetchedReferral
}

export const { resetState } = referralPacketSlice.actions

export default referralPacketSlice.reducer
